/**
 * Environment Badge Component
 * Displays a visual indicator of the current environment
 * Only visible in non-production environments
 */
import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import {
  DeveloperMode as DevIcon,
  Cloud as CloudIcon,
  Computer as LocalIcon,
  CheckCircle as ProdIcon,
  BugReport as QAIcon,
} from '@mui/icons-material';
import environmentService from '../../config/environment.config';

const EnvironmentBadge: React.FC = () => {
  // Don't show in production
  if (environmentService.isProduction) {
    return null;
  }

  const getEnvironmentConfig = () => {
    switch (environmentService.environment) {
      case 'local':
        return {
          label: 'LOCAL',
          color: 'info' as const,
          icon: <LocalIcon fontSize="small" />,
          description: 'Local Development (localhost:5004)',
        };
      case 'development':
        return {
          label: 'DEV',
          color: 'warning' as const,
          icon: <DevIcon fontSize="small" />,
          description: 'Development Server (3.18.116.148:5211)',
        };
      case 'qa':
        return {
          label: 'QA',
          color: 'secondary' as const,
          icon: <QAIcon fontSize="small" />,
          description: 'QA/Staging Server (18.116.72.136:5211)',
        };
      default:
        return {
          label: 'PROD',
          color: 'success' as const,
          icon: <ProdIcon fontSize="small" />,
          description: 'Production (admin.alphahealthai.com)',
        };
    }
  };

  const config = getEnvironmentConfig();
  const envConfig = environmentService.config;

  return (
    <Tooltip
      title={
        <Box sx={{ p: 1 }}>
          <Typography variant="caption" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {config.description}
          </Typography>
          <Typography variant="caption" component="div" sx={{ fontSize: '0.7rem' }}>
            <strong>Environment:</strong> {envConfig.name}
          </Typography>
          <Typography variant="caption" component="div" sx={{ fontSize: '0.7rem' }}>
            <strong>API:</strong> {envConfig.apiBaseUrl}
          </Typography>
          <Typography variant="caption" component="div" sx={{ fontSize: '0.7rem' }}>
            <strong>Version:</strong> {envConfig.appVersion}
          </Typography>
          <Typography variant="caption" component="div" sx={{ fontSize: '0.7rem' }}>
            <strong>Dev Features:</strong> {envConfig.showDevFeatures ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="caption" component="div" sx={{ fontSize: '0.7rem' }}>
            <strong>Analytics:</strong> {envConfig.enableAnalytics ? 'Yes' : 'No'}
          </Typography>
        </Box>
      }
      arrow
      placement="left"
    >
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 9999,
          fontWeight: 'bold',
          boxShadow: 3,
          cursor: 'help',
          '&:hover': {
            boxShadow: 6,
          },
        }}
      />
    </Tooltip>
  );
};

export default EnvironmentBadge;
